import { useEffect, type MutableRefObject } from "react";

/**
 * The handle a form fills with its own "mark up everything that is missing"
 * routine.
 *
 * Marking up belongs to the form — `touched` lives there, and so does the order
 * the first failing field is looked for in — but the page above it sometimes
 * has to ask for it. The rail's unsaved prompt is the case: its Save button
 * offers to store a form that may not be finishable, and pressing it has to
 * point at what is missing exactly as the form's own Save row does. See
 * `UnsavedChanges`.
 *
 * `null` while no form is mounted, so a caller holding one is asking a question
 * rather than assuming an answer.
 */
export type ProblemReport = MutableRefObject<(() => void) | null>;

/**
 * Keeps `handle` pointed at this form's reporter for as long as it is on
 * screen, and empties it again on the way out.
 *
 * No dependency array on purpose: `report` closes over the errors as they
 * stand, so the handle has to be re-pointed on every render — held from the
 * first one it would mark up whatever was missing when the form opened rather
 * than what is missing now.
 */
export const useProblemReport = (
  handle: ProblemReport | undefined,
  report: () => void
) => {
  useEffect(() => {
    if (!handle) return;

    handle.current = report;
    return () => {
      handle.current = null;
    };
  });
};
